import React, { useState, useRef, useMemo } from 'react';
import Icon from 'components/AppIcon';
import MusicVideoRenderer from 'pages/creator-dashboard/components/MusicVideoRenderer';
import {
  detectSuspiciousMedia,
  getEffectiveDuration as calcEffectiveDuration,
  isDurationLocked,
  DURATION_LIMITS,
  extractVisualKeywords,
} from 'utils/videoUtils';
import { resolveAudioUrl } from 'utils/sunoResolver';

const ACCEPTED_AUDIO = ['.mp3', '.wav', '.aac', '.m4a'];
const ACCEPTED_AUDIO_MIME = 'audio/mpeg,audio/wav,audio/aac,audio/mp4,audio/x-m4a';

const SYNC_MODES = [
  { id: 'beats', label: 'Beat Sync', icon: 'Activity', desc: 'Visuals react to rhythm and tempo' },
  { id: 'lyrics', label: 'Lyric Sync', icon: 'AlignLeft', desc: 'Visuals align with lyric meaning' },
  { id: 'both', label: 'Full Sync', icon: 'Layers', desc: 'Beat + lyric synchronization' },
];

const CAPTION_MODES = [
  { id: 'none', label: 'No Captions', icon: 'EyeOff' },
  { id: 'subtitles', label: 'Simple Subtitles', icon: 'Subtitles' },
  { id: 'karaoke', label: 'Karaoke Style', icon: 'Mic2' },
  { id: 'cinematic', label: 'Cinematic', icon: 'Film' },
  { id: 'animated', label: 'Animated Lyrics', icon: 'Sparkles' },
];

const DURATION_OPTIONS = [
  { id: 'full', label: 'Full Song', icon: 'Music', desc: 'Match entire track duration', premium: false },
  { id: '30s', label: 'First 30s', icon: 'Timer', desc: '30 second preview clip', premium: false },
  { id: '60s', label: 'First 60s', icon: 'Clock', desc: '60 second short video', premium: false },
  { id: 'custom', label: 'Custom', icon: 'Sliders', desc: 'Set your own duration', premium: true },
];

const VISUAL_STYLE_GROUPS = [
  {
    group: 'Abstract',
    icon: 'Sparkles',
    color: '#F43F5E',
    styles: [
      { id: 'particles', label: 'Particles', icon: 'Sparkles' },
      { id: 'waves', label: 'Waves', icon: 'Activity' },
      { id: 'nebula', label: 'Nebula', icon: 'Circle' },
      { id: 'geometric', label: 'Geometric', icon: 'Triangle' },
    ],
  },
  {
    group: 'Realistic',
    icon: 'Camera',
    color: '#06B6D4',
    styles: [
      { id: 'realistic-photos', label: 'Realistic Photos', icon: 'Image' },
      { id: 'cinematic-stock', label: 'Cinematic Stock', icon: 'Clapperboard' },
      { id: 'nature', label: 'Nature Footage', icon: 'Trees' },
      { id: 'urban', label: 'City / Urban', icon: 'Building2' },
      { id: 'documentary', label: 'Documentary', icon: 'Video' },
      { id: 'worship', label: 'Worship / Spiritual', icon: 'Sun' },
    ],
  },
  {
    group: 'Lyrics / Motion',
    icon: 'Type',
    color: '#8B5CF6',
    styles: [
      { id: 'animated-lyrics', label: 'Animated Lyrics', icon: 'AlignCenter' },
      { id: 'captioned-video', label: 'Captioned Video', icon: 'Subtitles' },
      { id: 'motion-typography', label: 'Motion Typography', icon: 'Type' },
    ],
  },
  {
    group: 'Hybrid',
    icon: 'Layers',
    color: '#10B981',
    styles: [
      { id: 'realistic-captions', label: 'Realistic + Captions', icon: 'Film' },
      { id: 'realistic-beats', label: 'Realistic + Beat FX', icon: 'Zap' },
      { id: 'visualizer-cinematic', label: 'Visualizer + Cinematic', icon: 'Wand2' },
    ],
  },
];

const GENERATION_STAGES = [
  { key: 'validating', label: 'Validating source', icon: 'Shield' },
  { key: 'importing', label: 'Importing audio', icon: 'Upload' },
  { key: 'analyzing', label: 'Analyzing audio', icon: 'Activity' },
  { key: 'gathering', label: 'Gathering visuals', icon: 'Image' },
  { key: 'scenes', label: 'Generating scenes', icon: 'Layers' },
  { key: 'rendering', label: 'Rendering video', icon: 'Film' },
  { key: 'merging', label: 'Merging audio + captions', icon: 'Merge' },
  { key: 'finalizing', label: 'Finalizing export', icon: 'Download' },
  { key: 'saving', label: 'Saving to library', icon: 'Library' },
];

const CAPTION_COLOR_THEMES = [
  { id: 'white', label: 'White', color: '#ffffff' },
  { id: 'yellow', label: 'Yellow', color: '#FFE600' },
  { id: 'cyan', label: 'Cyan', color: '#00E5FF' },
  { id: 'gold', label: 'Gold', color: '#F0E6C8' },
];

const CAPTION_PLACEMENTS = [
  { id: 'bottom', label: 'Bottom', icon: 'AlignBottom' },
  { id: 'center', label: 'Center', icon: 'AlignCenter' },
];

export default function MusicUpload({
  musicFile, onMusicFileChange,
  syncMode, onSyncModeChange,
  outputType, onOutputTypeChange,
  lyricsText, onLyricsTextChange,
  disabled, onVideoGenerated,
  testMode = false,
  userTier = 'free',
}) {
  const fileInputRef = useRef(null);
  const lyricsFileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [inputMode, setInputMode] = useState('upload'); // 'upload' | 'url'
  const [musicUrl, setMusicUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlNote, setUrlNote] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [visualStyle, setVisualStyle] = useState('particles');
  const [activeStyleGroup, setActiveStyleGroup] = useState(0);
  const [durationMode, setDurationMode] = useState('full');
  const [customDuration, setCustomDuration] = useState(60);
  const [captionMode, setCaptionMode] = useState('none');
  const [captionStyle, setCaptionStyle] = useState({
    fontSize: 28,
    fontFamily: 'sans-serif',
    colorTheme: 'white',
    placement: 'bottom',
  });
  const [showCaptionStyling, setShowCaptionStyling] = useState(false);
  const [genStage, setGenStage] = useState(null);
  const [genPercent, setGenPercent] = useState(0);
  const [isRendering, setIsRendering] = useState(false);
  const [renderDone, setRenderDone] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [mediaWarning, setMediaWarning] = useState(null);
  const [showKeywords, setShowKeywords] = useState(false);

  const canUseCustom = testMode || userTier === 'studio' || userTier === 'enterprise';

  // Derive semantic keywords for display
  const semanticKeywords = React.useMemo(() => {
    const isRealistic = !['particles', 'waves', 'nebula', 'geometric']?.includes(visualStyle);
    if (!isRealistic) return [];
    return extractVisualKeywords({
      title: videoTitle,
      prompt: '',
      mood: '',
      genre: '',
      lyrics: lyricsText,
    });
  }, [visualStyle, videoTitle, lyricsText]);

  const handleFileSelect = (file) => {
    if (!file) return;
    const ext = '.' + file?.name?.split('.')?.pop()?.toLowerCase();
    if (!ACCEPTED_AUDIO?.includes(ext)) return;
    if (detectSuspiciousMedia(file)) {
      setMediaWarning('Uploaded media may contain copyrighted or restricted content. Please upload original, licensed, or public-domain media only.');
      return;
    }
    setMediaWarning(null);
    onMusicFileChange(file);
    // Auto-generate title from filename if title is empty
    if (!videoTitle) {
      const autoTitle = file?.name
        ?.replace(/\.[^.]+$/, '')
        ?.replace(/[-_]/g, ' ')
        ?.replace(/\b\w/g, (c) => c?.toUpperCase());
      setVideoTitle(autoTitle || '');
    }
    setRenderDone(false);
    setRenderError(null);
    setGeneratedVideo(null);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFileSelect(file);
  };

  const handleUrlImport = async () => {
    if (!musicUrl?.trim()) return;
    setUrlError('');
    setUrlNote('');
    setUrlLoading(true);
    try {
      const result = await resolveAudioUrl(musicUrl?.trim());
      if (!result?.success) {
        setUrlError(result?.error);
        setUrlLoading(false);
        return;
      }
      const resolvedUrl = result?.audioUrl;
      const isSuno = result?.provider === 'suno';
      const fileName = isSuno
        ? `suno-${result?.songId || 'audio'}.mp3`
        : (resolvedUrl?.split('/')?.pop()?.split('?')?.[0] || 'imported-audio.mp3');

      const mockFile = {
        name: fileName,
        size: 0,
        url: resolvedUrl,
        isUrlImport: true,
        provider: result?.provider,
        songId: result?.songId || null,
      };
      onMusicFileChange(mockFile);

      // Auto-generate title from filename/songId if title is empty
      if (!videoTitle) {
        const autoTitle = fileName
          ?.replace(/\.[^.]+$/, '')
          ?.replace(/[-_]/g, ' ')
          ?.replace(/\b\w/g, (c) => c?.toUpperCase());
        setVideoTitle(autoTitle || (isSuno ? 'Suno Music Video' : 'Music Video'));
      }

      if (result?.note) setUrlNote(result?.note);
      setRenderDone(false);
      setRenderError(null);
      setGeneratedVideo(null);
    } catch (err) {
      setUrlError('Could not resolve this URL. Please check the link or upload the file directly.');
    }
    setUrlLoading(false);
  };

  const handleLyricsFile = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onLyricsTextChange(ev?.target?.result || '');
    reader?.readAsText(file);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const getEffectiveDuration = (audioDuration) => {
    return calcEffectiveDuration(audioDuration, durationMode, customDuration, userTier, testMode);
  };

  const handleProgress = ({ stage, percent, stageIndex }) => {
    setIsRendering(true);
    setGenPercent(percent);
    if (stageIndex !== undefined) {
      setGenStage(GENERATION_STAGES?.[stageIndex]?.label || stage);
    } else {
      setGenStage(stage);
    }
  };

  const handleVideoReady = (result) => {
    setIsRendering(false);
    setRenderDone(true);
    setRenderError(null);
    setGeneratedVideo(result);
    onVideoGenerated?.({
      ...result,
      title: videoTitle || result?.title || 'My Music Video',
      captionMode,
      captionStyle,
      durationMode,
      visualStyle,
      safeSource: 'loopnest-generated',
    });
  };

  const handleRenderError = (err) => {
    setIsRendering(false);
    setRenderDone(false);
    setRenderError(err || 'Rendering failed. Please try again.');
  };

  const currentStageIndex = GENERATION_STAGES?.findIndex((s) => s?.label === genStage);
  const activeGroup = VISUAL_STYLE_GROUPS?.[activeStyleGroup];

  const tierLabel = testMode ? 'TEST MODE' : (DURATION_LIMITS?.[userTier]?.label || 'Free');

  return (
    <div className="flex flex-col gap-4">
      {/* Test Mode Banner */}
      {testMode && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)' }}>
          <Icon name="FlaskConical" size={14} color="#F59E0B" />
          <span className="text-xs font-semibold" style={{ color: '#F59E0B', fontFamily: 'var(--font-caption)' }}>TEST MODE — All premium features unlocked for testing</span>
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B' }}>No billing</span>
        </div>
      )}
      {/* Video Title */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
          <Icon name="Type" size={14} className="inline mr-1.5" color="#F43F5E" />
          Video Title <span className="text-xs font-normal ml-1" style={{ color: 'var(--color-muted-foreground)' }}>(auto-filled from song name)</span>
        </p>
        <input
          type="text"
          value={videoTitle}
          onChange={(e) => setVideoTitle(e?.target?.value)}
          placeholder="Enter a title for your music video..."
          disabled={disabled || isRendering}
          className="w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all"
          style={{
            background: 'var(--color-card)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-foreground)',
            fontFamily: 'var(--font-body)',
          }}
        />
      </div>
      {/* Music Input Mode Toggle */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
          <Icon name="Music" size={14} className="inline mr-1.5" color="#F43F5E" />
          Music Source
        </p>
        <div className="flex gap-2 mb-3">
          {[{ id: 'upload', label: 'Upload File', icon: 'Upload' }, { id: 'url', label: 'Import URL', icon: 'Link' }]?.map((m) => (
            <button
              key={m?.id}
              onClick={() => { setInputMode(m?.id); setUrlError(''); setUrlNote(''); }}
              disabled={disabled || isRendering}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{
                border: `1px solid ${inputMode === m?.id ? '#F43F5E' : 'var(--color-border)'}`,
                background: inputMode === m?.id ? 'rgba(244,63,94,0.1)' : 'var(--color-muted)',
                color: inputMode === m?.id ? '#F43F5E' : 'var(--color-muted-foreground)',
              }}
            >
              <Icon name={m?.icon} size={13} color={inputMode === m?.id ? '#F43F5E' : 'var(--color-muted-foreground)'} />
              {m?.label}
            </button>
          ))}
        </div>

        {inputMode === 'upload' ? (
          musicFile && !musicFile?.isUrlImport ? (
            <div className="flex items-center gap-3 p-3 rounded-xl border" style={{ background: 'rgba(244,63,94,0.06)', borderColor: 'rgba(244,63,94,0.3)' }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(244,63,94,0.15)' }}>
                <Icon name="Music" size={18} color="#F43F5E" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}>{musicFile?.name}</p>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{formatFileSize(musicFile?.size)} · User upload (safe)</p>
              </div>
              <button
                onClick={() => { onMusicFileChange(null); setRenderDone(false); setRenderError(null); setGeneratedVideo(null); }}
                disabled={disabled || isRendering}
                className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all flex-shrink-0"
              >
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !disabled && !isRendering && fileInputRef?.current?.click()}
              className="flex flex-col items-center justify-center gap-2 p-5 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200"
              style={{
                borderColor: dragOver ? '#F43F5E' : 'var(--color-border)',
                background: dragOver ? 'rgba(244,63,94,0.06)' : 'var(--color-muted)',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(244,63,94,0.12)' }}>
                <Icon name="Upload" size={20} color="#F43F5E" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}>
                  Drop your music file here
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  or click to browse
                </p>
              </div>
              <div className="flex gap-1.5 flex-wrap justify-center">
                {ACCEPTED_AUDIO?.map((ext) => (
                  <span
                    key={ext}
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(244,63,94,0.1)', color: '#F43F5E', fontFamily: 'var(--font-caption)' }}
                  >
                    {ext}
                  </span>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <input
                type="url"
                value={musicUrl}
                onChange={(e) => { setMusicUrl(e?.target?.value); setUrlError(''); setUrlNote(''); }}
                placeholder="Paste audio URL or Suno song link..."
                disabled={disabled || isRendering || urlLoading}
                className="flex-1 rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all"
                style={{ background: 'var(--color-card)', borderColor: urlError ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}
              />
              <button
                onClick={handleUrlImport}
                disabled={disabled || isRendering || urlLoading || !musicUrl?.trim()}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: '#F43F5E', color: 'white' }}
              >
                {urlLoading ? <Icon name="Loader" size={14} color="white" /> : <Icon name="Download" size={14} color="white" />}
                Import
              </button>
            </div>
            {urlError && (
              <div className="flex items-start gap-2 p-2.5 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <Icon name="AlertCircle" size={14} color="#EF4444" className="flex-shrink-0 mt-0.5" />
                <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'var(--font-caption)' }}>{urlError}</p>
              </div>
            )}
            {urlNote && (
              <div className="flex items-start gap-2 p-2.5 rounded-xl" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <Icon name="Info" size={14} color="#F59E0B" className="flex-shrink-0 mt-0.5" />
                <p className="text-xs" style={{ color: '#F59E0B', fontFamily: 'var(--font-caption)' }}>{urlNote}</p>
              </div>
            )}
            {musicFile?.isUrlImport && (
              <div className="flex items-center gap-2 p-2.5 rounded-xl" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <Icon name="CheckCircle2" size={14} color="var(--color-success)" />
                <p className="text-xs" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>
                  Audio imported: {musicFile?.name}
                  {musicFile?.provider === 'suno' && ' (Suno)'}
                </p>
              </div>
            )}
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              Supported: direct mp3/wav/m4a/aac links · Suno song links (suno.com/song/...)
            </p>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept={ACCEPTED_AUDIO_MIME} className="hidden" onChange={(e) => handleFileSelect(e?.target?.files?.[0])} disabled={disabled} />
      </div>
      {/* Media Safety Warning */}
      {mediaWarning && (
        <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <Icon name="ShieldAlert" size={16} color="#EF4444" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold" style={{ color: '#EF4444' }}>Media Safety Warning</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{mediaWarning}</p>
          </div>
          <button onClick={() => setMediaWarning(null)} className="ml-auto flex-shrink-0"><Icon name="X" size={13} color="var(--color-muted-foreground)" /></button>
        </div>
      )}
      {/* Duration Options */}
      {musicFile && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              <Icon name="Clock" size={14} className="inline mr-1.5" color="#F43F5E" />
              Video Duration
            </p>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: testMode ? 'rgba(245,158,11,0.15)' : 'var(--color-muted)', color: testMode ? '#F59E0B' : 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {tierLabel}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {DURATION_OPTIONS?.map((opt) => {
              const active = durationMode === opt?.id;
              const locked = isDurationLocked(opt?.id, userTier, testMode);
              return (
                <button
                  key={opt?.id}
                  onClick={() => !locked && !isRendering && setDurationMode(opt?.id)}
                  disabled={isRendering || locked}
                  className="flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all"
                  style={{
                    borderColor: active ? '#F43F5E' : 'var(--color-border)',
                    background: active ? 'rgba(244,63,94,0.1)' : locked ? 'var(--color-muted)' : 'var(--color-card)',
                    opacity: locked ? 0.5 : 1,
                  }}
                >
                  <Icon name={locked ? 'Lock' : opt?.icon} size={14} color={active ? '#F43F5E' : 'var(--color-muted-foreground)'} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold" style={{ color: active ? '#F43F5E' : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{opt?.label}</p>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{locked ? 'Studio+ only' : opt?.desc}</p>
                  </div>
                  {active && <Icon name="CheckCircle2" size={13} color="#F43F5E" />}
                </button>
              );
            })}
          </div>
          {durationMode === 'custom' && canUseCustom && (
            <div className="mt-2 flex items-center gap-3">
              <span className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Duration:</span>
              <input
                type="range" min={10} max={testMode ? 600 : (DURATION_LIMITS?.[userTier]?.max || 120)} step={10} value={customDuration}
                onChange={(e) => setCustomDuration(Number(e?.target?.value))}
                className="flex-1 accent-pink-500"
              />
              <span className="text-xs font-bold w-12 text-right" style={{ color: '#F43F5E', fontFamily: 'var(--font-data)' }}>
                {customDuration >= 60 ? `${Math.floor(customDuration / 60)}m ${customDuration % 60}s` : `${customDuration}s`}
              </span>
            </div>
          )}
        </div>
      )}
      {/* Visual Style Groups */}
      {musicFile && (
        <div>
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            <Icon name="Palette" size={14} className="inline mr-1.5" color="#F43F5E" />
            Visual Style
          </p>
          {/* Group tabs */}
          <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1">
            {VISUAL_STYLE_GROUPS?.map((grp, idx) => (
              <button
                key={grp?.group}
                onClick={() => setActiveStyleGroup(idx)}
                disabled={isRendering}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
                style={{
                  background: activeStyleGroup === idx ? grp?.color : 'var(--color-muted)',
                  color: activeStyleGroup === idx ? 'white' : 'var(--color-muted-foreground)',
                  border: `1px solid ${activeStyleGroup === idx ? grp?.color : 'var(--color-border)'}`,
                }}
              >
                <Icon name={grp?.icon} size={11} color={activeStyleGroup === idx ? 'white' : 'var(--color-muted-foreground)'} />
                {grp?.group}
              </button>
            ))}
          </div>
          {/* Style grid */}
          <div className="grid grid-cols-2 gap-2">
            {activeGroup?.styles?.map((vs) => {
              const active = visualStyle === vs?.id;
              const grpColor = activeGroup?.color;
              return (
                <button
                  key={vs?.id}
                  onClick={() => !isRendering && setVisualStyle(vs?.id)}
                  disabled={isRendering}
                  className="flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all"
                  style={{
                    borderColor: active ? grpColor : 'var(--color-border)',
                    background: active ? `${grpColor}12` : 'var(--color-card)',
                    boxShadow: active ? `0 0 0 2px ${grpColor}30` : 'none',
                  }}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: active ? grpColor : `${grpColor}20` }}>
                    <Icon name={vs?.icon} size={13} color={active ? '#fff' : grpColor} />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: active ? grpColor : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{vs?.label}</span>
                  {active && <Icon name="CheckCircle2" size={13} color={grpColor} className="ml-auto" />}
                </button>
              );
            })}
          </div>

          {/* AI Semantic Keywords for realistic styles */}
          {semanticKeywords?.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowKeywords((v) => !v)}
                className="flex items-center gap-1.5 text-xs transition-all hover:opacity-80"
                style={{ color: 'var(--color-muted-foreground)' }}
              >
                <Icon name="Sparkles" size={11} color="#8B5CF6" />
                <span style={{ color: '#8B5CF6' }}>AI visual keywords detected</span>
                <Icon name={showKeywords ? 'ChevronUp' : 'ChevronDown'} size={11} color="var(--color-muted-foreground)" />
              </button>
              {showKeywords && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {semanticKeywords?.map((kw) => (
                    <span key={kw} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6', fontFamily: 'var(--font-caption)' }}>{kw}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Visual Sync Mode */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Visual Sync Mode</p>
        <div className="flex gap-2">
          {SYNC_MODES?.map((sm) => {
            const active = syncMode === sm?.id;
            return (
              <button
                key={sm?.id}
                onClick={() => !disabled && !isRendering && onSyncModeChange(sm?.id)}
                disabled={disabled || isRendering}
                className="flex-1 flex flex-col items-center gap-1 p-2.5 rounded-xl border text-center transition-all"
                style={{
                  borderColor: active ? '#F43F5E' : 'var(--color-border)',
                  background: active ? 'rgba(244,63,94,0.08)' : 'var(--color-card)',
                }}
              >
                <Icon name={sm?.icon} size={15} color={active ? '#F43F5E' : 'var(--color-muted-foreground)'} />
                <span className="text-xs font-semibold" style={{ color: active ? '#F43F5E' : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{sm?.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Caption Mode */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
          <Icon name="Subtitles" size={14} className="inline mr-1.5" color="#F43F5E" />
          Caption Style
        </p>
        <div className="flex gap-1.5 flex-wrap">
          {CAPTION_MODES?.map((cm) => {
            const active = captionMode === cm?.id;
            return (
              <button
                key={cm?.id}
                onClick={() => { if (!isRendering) { setCaptionMode(cm?.id); setShowCaptionStyling(cm?.id !== 'none'); } }}
                disabled={isRendering}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: active ? '#F43F5E' : 'var(--color-muted)',
                  color: active ? 'white' : 'var(--color-muted-foreground)',
                  border: `1px solid ${active ? '#F43F5E' : 'var(--color-border)'}`,
                }}
              >
                <Icon name={cm?.icon} size={11} color={active ? 'white' : 'var(--color-muted-foreground)'} />
                {cm?.label}
              </button>
            );
          })}
        </div>

        {/* Caption Styling Options */}
        {captionMode !== 'none' && showCaptionStyling && (
          <div className="mt-3 p-3 rounded-xl flex flex-col gap-3" style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
            <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Caption Styling</p>

            {/* Font size */}
            <div className="flex items-center gap-3">
              <span className="text-xs w-20" style={{ color: 'var(--color-muted-foreground)' }}>Font Size</span>
              <input
                type="range" min={18} max={48} step={2}
                value={captionStyle?.fontSize || 28}
                onChange={(e) => setCaptionStyle((s) => ({ ...s, fontSize: Number(e?.target?.value) }))}
                className="flex-1 accent-pink-500"
              />
              <span className="text-xs font-bold w-8 text-right" style={{ color: '#F43F5E' }}>{captionStyle?.fontSize || 28}px</span>
            </div>

            {/* Color theme */}
            <div className="flex items-center gap-2">
              <span className="text-xs w-20" style={{ color: 'var(--color-muted-foreground)' }}>Color</span>
              <div className="flex gap-1.5">
                {CAPTION_COLOR_THEMES?.map((ct) => (
                  <button
                    key={ct?.id}
                    onClick={() => setCaptionStyle((s) => ({ ...s, colorTheme: ct?.id }))}
                    className="w-6 h-6 rounded-full border-2 transition-all"
                    style={{
                      background: ct?.color,
                      borderColor: captionStyle?.colorTheme === ct?.id ? '#F43F5E' : 'transparent',
                      boxShadow: captionStyle?.colorTheme === ct?.id ? '0 0 0 2px rgba(244,63,94,0.4)' : 'none',
                    }}
                    title={ct?.label}
                  />
                ))}
              </div>
            </div>

            {/* Placement */}
            <div className="flex items-center gap-2">
              <span className="text-xs w-20" style={{ color: 'var(--color-muted-foreground)' }}>Placement</span>
              <div className="flex gap-1.5">
                {CAPTION_PLACEMENTS?.map((cp) => (
                  <button
                    key={cp?.id}
                    onClick={() => setCaptionStyle((s) => ({ ...s, placement: cp?.id }))}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      background: captionStyle?.placement === cp?.id ? 'rgba(244,63,94,0.15)' : 'var(--color-card)',
                      color: captionStyle?.placement === cp?.id ? '#F43F5E' : 'var(--color-muted-foreground)',
                      border: `1px solid ${captionStyle?.placement === cp?.id ? '#F43F5E' : 'var(--color-border)'}`,
                    }}
                  >
                    <Icon name={cp?.icon} size={11} color={captionStyle?.placement === cp?.id ? '#F43F5E' : 'var(--color-muted-foreground)'} />
                    {cp?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Lyrics */}
      {(syncMode === 'lyrics' || syncMode === 'both' || captionMode !== 'none') && (
        <div className="animate-fade-in-up">
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            Lyrics / Captions
            <span className="ml-1.5 text-xs font-normal" style={{ color: 'var(--color-muted-foreground)' }}>(optional)</span>
          </p>
          <textarea
            value={lyricsText}
            onChange={(e) => onLyricsTextChange(e?.target?.value)}
            placeholder="Paste your lyrics here, or upload a .txt file below...\n\nTip: Use [00:12.34] timestamps for precise sync (LRC format)"
            rows={4}
            disabled={disabled || isRendering}
            className="w-full resize-none rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(244,63,94,0.4)] transition-all"
            style={{ fontFamily: 'var(--font-body)' }}
          />
          <button
            onClick={() => !disabled && !isRendering && lyricsFileRef?.current?.click()}
            disabled={disabled || isRendering}
            className="mt-2 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:opacity-80 disabled:opacity-40"
            style={{ borderColor: 'rgba(244,63,94,0.4)', color: '#F43F5E', background: 'rgba(244,63,94,0.06)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="FileText" size={12} color="#F43F5E" />
            Upload .txt lyrics file
          </button>
          <input ref={lyricsFileRef} type="file" accept=".txt,text/plain" className="hidden" onChange={handleLyricsFile} disabled={disabled} />
        </div>
      )}
      {/* Real Video Renderer */}
      {musicFile && (
        <div className="animate-fade-in-up">
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            <Icon name="Wand2" size={14} className="inline mr-1.5" color="#F43F5E" />
            Video Generator
          </p>

          {/* Generation stages */}
          {isRendering && (
            <div className="mb-3 flex flex-col gap-2">
              <div className="flex flex-wrap gap-1.5">
                {GENERATION_STAGES?.map((stage, i) => {
                  const isDone = i < currentStageIndex;
                  const isCurrent = i === currentStageIndex;
                  return (
                    <div
                      key={stage?.key}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                      style={{
                        background: isDone ? 'rgba(16,185,129,0.1)' : isCurrent ? 'rgba(244,63,94,0.12)' : 'var(--color-muted)',
                        color: isDone ? 'var(--color-success)' : isCurrent ? '#F43F5E' : 'var(--color-muted-foreground)',
                      }}
                    >
                      <Icon
                        name={isDone ? 'CheckCircle2' : isCurrent ? 'Loader' : 'Circle'}
                        size={10}
                        color={isDone ? 'var(--color-success)' : isCurrent ? '#F43F5E' : 'var(--color-muted-foreground)'}
                      />
                      <span className="hidden sm:inline">{stage?.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${genPercent}%`, background: 'linear-gradient(90deg, #F43F5E, #8B5CF6)' }}
                />
              </div>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                {genStage} — {Math.round(genPercent)}%
              </p>
            </div>
          )}

          {/* Render error state */}
          {renderError && !isRendering && (
            <div className="mb-3 flex items-start gap-2 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <Icon name="AlertCircle" size={16} color="#EF4444" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold" style={{ color: '#EF4444' }}>Rendering failed</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{renderError}</p>
              </div>
              <button
                onClick={() => { setRenderError(null); setGeneratedVideo(null); }}
                className="text-xs px-2 py-1 rounded-lg flex-shrink-0"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Success state — only shown when 100% complete */}
          {renderDone && generatedVideo && !isRendering && (
            <div className="mb-3 flex items-center gap-2 p-2.5 rounded-xl" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
              <div className="flex-1">
                <p className="text-xs font-semibold" style={{ color: 'var(--color-success)' }}>Video generated successfully!</p>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                  Duration: {Math.round(generatedVideo?.duration || 0)}s · BPM: ~{generatedVideo?.bpm || '?'} · {visualStyle}
                </p>
              </div>
              <button
                onClick={() => { setRenderDone(false); setGeneratedVideo(null); setRenderError(null); }}
                className="text-xs px-2 py-1 rounded-lg"
                style={{ background: 'rgba(16,185,129,0.15)', color: 'var(--color-success)' }}
              >
                Re-render
              </button>
            </div>
          )}

          <MusicVideoRenderer
            audioFile={musicFile}
            visualStyle={visualStyle}
            durationMode={durationMode}
            customDuration={customDuration}
            captionMode={captionMode}
            captionStyle={captionStyle}
            lyricsText={lyricsText}
            videoTitle={videoTitle}
            getEffectiveDuration={getEffectiveDuration}
            onProgress={handleProgress}
            onVideoReady={handleVideoReady}
            onError={handleRenderError}
          />
        </div>
      )}
    </div>
  );
}
